/**
 * Safe wrappers around `sessionStorage` for JSON-serializable values.
 *
 * `sessionStorage` can throw in private browsing modes, when the quota
 * is exhausted, or when storage is blocked by browser policy. These
 * helpers swallow those errors and return a default so callers can
 * stay fully synchronous and never crash the funnel.
 *
 * Note: only use for JSON-serializable values — raw string usages that
 * cannot survive a `JSON.parse` round-trip should keep their own
 * `try/catch` around `sessionStorage.getItem` directly.
 */

/**
 * Reads and JSON-parses a value from `sessionStorage`. Returns the
 * `defaultValue` when the key is missing, storage is unavailable, or
 * the stored payload is not valid JSON.
 */
export function getSessionItem<T>(key: string, defaultValue: T): T {
  try {
    const raw = sessionStorage.getItem(key);
    if (raw === null) return defaultValue;
    return JSON.parse(raw) as T;
  } catch {
    return defaultValue;
  }
}

/**
 * JSON-stringifies and writes a value to `sessionStorage`. Silently
 * no-ops when storage is blocked (private browsing, quota exceeded).
 */
export function setSessionItem(key: string, value: unknown): void {
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    // storage blocked (private browsing, quota)
  }
}
